import { useState, type FormEvent } from 'react';
import type {
  CreatePrincipalInput,
  PrincipalRiskLevel,
} from '@dsr/shared-types';
import { useCreatePrincipal } from '../../api/queries/principals';
import { useOrganizations } from '../../api/queries/clients';
import { DateInput } from '../DateInput';
import { toast } from '../Toasts';
import { ApiError } from '../../api/client';
import { Button, Dialog, FormField, Input, Textarea } from '../ui';
import { RISK_LEVELS } from './meta';

// Create-principal dialog. Extracted from the old PrincipalsPage so the
// Directory › Individuals page can reuse it. `defaultOrgId` pre-selects the
// affiliated client when opened from an org context.
export function CreatePrincipalModal({
  defaultOrgId,
  onClose,
}: {
  defaultOrgId?: number;
  onClose: () => void;
}): JSX.Element {
  const { data: orgData } = useOrganizations();
  const [organizationId, setOrganizationId] = useState<number | ''>(
    defaultOrgId ?? '',
  );
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [alias, setAlias] = useState('');
  const [dob, setDob] = useState<number | null>(null);
  const [riskLevel, setRiskLevel] = useState<PrincipalRiskLevel>('low');
  const [medicalNotes, setMedicalNotes] = useState('');

  const create = useCreatePrincipal();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const input: CreatePrincipalInput = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      risk_level: riskLevel,
    };
    if (organizationId !== '') input.organization_id = organizationId;
    if (alias.trim()) input.alias = alias.trim();
    if (dob !== null) input.dob = dob;
    if (medicalNotes.trim()) input.medical_notes = medicalNotes.trim();

    try {
      await create.mutateAsync(input);
      toast.success('Principal created.');
      onClose();
    } catch (err) {
      toast.danger(
        err instanceof ApiError ? err.message : 'Failed to create principal.',
      );
    }
  };

  return (
    <Dialog
      title="New principal"
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-principal-form"
            variant="primary"
            disabled={
              create.isPending ||
              firstName.trim().length === 0 ||
              lastName.trim().length === 0
            }
          >
            {create.isPending ? 'Saving…' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="create-principal-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="First name" required>
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField label="Last name" required>
          <Input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </FormField>
        <FormField label="Alias">
          <Input value={alias} onChange={(e) => setAlias(e.target.value)} />
        </FormField>
        <FormField label="Client">
          <select
            className="form-input"
            value={organizationId}
            onChange={(e) =>
              setOrganizationId(e.target.value ? Number(e.target.value) : '')
            }
          >
            <option value="">Unaffiliated</option>
            {(orgData?.organizations ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Risk level">
          <select
            className="form-input"
            value={riskLevel}
            onChange={(e) => setRiskLevel(e.target.value as PrincipalRiskLevel)}
          >
            {RISK_LEVELS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Date of birth">
          <DateInput value={dob} onChange={setDob} />
        </FormField>
        <label className="form-field form-field-full">
          <span>Medical notes</span>
          <Textarea
            rows={3}
            value={medicalNotes}
            onChange={(e) => setMedicalNotes(e.target.value)}
          />
        </label>
      </form>
    </Dialog>
  );
}
